
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface HeaderProps {
  cartItemsCount: number;
  onCartClick: () => void;
  onLogout?: () => void;
}

export default function Header({ cartItemsCount, onCartClick, onLogout }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogoClick = () => {
    navigate('/shop');
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="px-3 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo - Mobile optimiert */}
          <div className="flex items-center flex-1 min-w-0">
            <button 
              onClick={() => navigate('/')}
              className="text-lg sm:text-2xl font-bold text-orange-600 hover:text-orange-700 transition-colors cursor-pointer truncate" 
              style={{ fontFamily: 'Pacifico, serif' }}
            >
              <span className="hidden sm:inline">Dä Snäckomat Shop</span>
              <span className="sm:hidden">Snäckomat</span>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
            <a
              href="https://snaeckomat.ch/ueber-uns"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-gray-900 transition-colors text-sm flex items-center gap-1 whitespace-nowrap"
            >
              Über uns
              <i className="ri-external-link-line text-xs"></i>
            </a>
            <a
              href="https://snaeckomat.ch/kontakt"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-gray-900 transition-colors text-sm flex items-center gap-1 whitespace-nowrap"
            >
              Kontakt
              <i className="ri-external-link-line text-xs"></i>
            </a>
          </nav>

          {/* Right side buttons - Mobile optimiert */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Cart Button - Mobile optimiert */}
            <button
              onClick={onCartClick}
              className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <i className="ri-shopping-cart-line text-xl sm:text-xl"></i>
              {cartItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]">
                  {cartItemsCount > 99 ? '99+' : cartItemsCount}
                </span>
              )}
            </button>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>

            {/* Desktop Logout Button */}
            {onLogout && (
              <button
                onClick={onLogout}
                className="hidden md:flex items-center px-3 lg:px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:border-gray-400 transition-colors whitespace-nowrap"
              >
                <i className="ri-logout-box-line mr-1 lg:mr-2"></i>
                <span className="hidden lg:inline">Abmelden</span>
                <span className="lg:hidden">Aus</span>
              </button>
            )}
          </div>
        </div>

        {/* Mobile menu - Verbessert */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-3 bg-white">
            <div className="flex flex-col space-y-3">
              <a
                href="https://snaeckomat.ch/ueber-uns"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-900 transition-colors text-sm flex items-center gap-2 py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                <i className="ri-information-line w-5 h-5 flex items-center justify-center"></i>
                <span>Über uns</span>
                <i className="ri-external-link-line text-xs ml-auto"></i>
              </a>
              <a
                href="https://snaeckomat.ch/kontakt"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-900 transition-colors text-sm flex items-center gap-2 py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                <i className="ri-customer-service-line w-5 h-5 flex items-center justify-center"></i>
                <span>Kontakt</span>
                <i className="ri-external-link-line text-xs ml-auto"></i>
              </a>
              {onLogout && (
                <button
                  onClick={() => {
                    onLogout();
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm gap-2 py-2 text-left"
                >
                  <i className="ri-logout-box-line w-5 h-5 flex items-center justify-center"></i>
                  <span>Abmelden</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
